import {Sidebar} from '@sollapay/ui/components';
import {useLocation, useNavigate} from 'react-router-dom';

import {useSidebarModePreference} from '../hooks/useSidebarModePreference';

import type {NavGroupConfig, NavItemConfig} from '../types/nav-item.types';
import type {SidebarNavGroup, SidebarProps} from '@sollapay/ui/components';
import type {FC, ReactNode} from 'react';

export interface AppSidebarProps {
  logo: ReactNode;
  collapsedLogo?: ReactNode;
  footer?: ReactNode;
  navItems?: NavItemConfig[];
  navGroups?: NavGroupConfig[];
  storageKey?: string;
  children?: ReactNode;
  variant?: SidebarProps['variant'];
  isLoading?: SidebarProps['isLoading'];
}

export const AppSidebar: FC<AppSidebarProps> = ({
  logo,
  collapsedLogo,
  footer,
  navItems,
  navGroups,
  storageKey = 'sidebar:mode',
  children,
  variant,
  isLoading
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {mode, setPreferredMode} = useSidebarModePreference(storageKey);

  let groups: SidebarNavGroup[];

  if (navGroups) {
    groups = navGroups.map(group => mapGroupToSidebarNavGroup(group, location.pathname, navigate));
  } else {
    groups = [
      {
        id: 'main',
        items: (navItems ?? []).map(item => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          isActive: isPathActive(location.pathname, item.path),
          disabled: item.disabled,
          badges: item.badges,
          onClick: () => {
            navigate(item.path);
          }
        }))
      }
    ];
  }

  const sidebarProps: SidebarProps = {
    logo,
    collapsedLogo,
    groups,
    footer,
    children,
    variant,
    isLoading,
    open: mode === 'expanded',
    onOpenChange: (isOpen: boolean) => setPreferredMode(isOpen ? 'expanded' : 'collapsed')
  };

  return <Sidebar {...sidebarProps} />;
};

function isPathActive(currentPath: string, itemPath: string): boolean {
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

function mapGroupToSidebarNavGroup(
  group: NavGroupConfig,
  currentPath: string,
  navigate: ReturnType<typeof useNavigate>
): SidebarNavGroup {
  return {
    id: group.id,
    label: group.label,
    items: group.items.map(item => ({
      id: item.id,
      label: item.label,
      icon: item.icon,
      isActive: isPathActive(currentPath, item.path),
      disabled: item.disabled,
      badges: item.badges,
      onClick: () => {
        navigate(item.path);
      }
    }))
  };
}
